import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoc, doc, updateDoc } from 'firebase/firestore';
import styled from 'styled-components';
import { db } from '../../firebase/config';
import Button from '../../components/ui/Button';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin: 0;
`;

const Field = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.5rem;
`;

const Value = styled.div`
  font-size: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${({ $status, theme }) => 
    $status === 'approved' ? theme.colors.success + '20' :
    $status === 'pending' ? theme.colors.warning + '20' :
    theme.colors.danger + '20'};
  color: ${({ $status, theme }) => 
    $status === 'approved' ? theme.colors.success :
    $status === 'pending' ? theme.colors.warning :
    theme.colors.danger};
  display: inline-block;
`;

const PartnerDetail = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const partnerDoc = await getDoc(doc(db, 'partners', partnerId));
        
        if (partnerDoc.exists()) {
          setPartner({ id: partnerDoc.id, ...partnerDoc.data() });
        } else {
          setError('Partner not found');
        }
      } catch (err) {
        console.error('Error fetching partner:', err);
        setError('Failed to load partner details');
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [partnerId]);

  const handleApprove = async () => {
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        pendingApproval: false,
        rejected: false
      });
      
      setPartner(prev => ({
        ...prev,
        pendingApproval: false,
        rejected: false
      }));
      
      toast.success('Partner has been approved');
    } catch (err) {
      console.error('Error approving partner:', err);
      toast.error('Failed to approve partner');
    }
  };

  const handleReject = async () => {
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        pendingApproval: false,
        rejected: true
      });
      
      setPartner(prev => ({
        ...prev,
        pendingApproval: false,
        rejected: true
      }));
      
      toast.success('Partner has been rejected');
    } catch (err) {
      console.error('Error rejecting partner:', err);
      toast.error('Failed to reject partner');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const getPartnerStatus = () => {
    if (!partner) return 'pending';
    if (partner.rejected) return 'rejected';
    if (partner.pendingApproval) return 'pending';
    return 'approved';
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>{partner?.displayName || 'Partner Detail'}</Title>
          <StatusBadge $status={getPartnerStatus()}>
            {partner?.rejected ? 'Rejected' : 
             partner?.pendingApproval ? 'Pending' : 'Approved'}
          </StatusBadge>
        </div>
        <Button onClick={() => navigate('/admin/partners')}>Back to Partners</Button>
      </Header>

      <Card>
        <Field>
          <Label>Email</Label>
          <Value>{partner?.email || 'N/A'}</Value>
        </Field>
        
        <Field>
          <Label>Phone</Label>
          <Value>{partner?.phone || 'N/A'}</Value>
        </Field>
        
        <Field>
          <Label>Location</Label>
          <Value>{partner?.location?.address || 'N/A'}</Value>
        </Field>
        
        <Field>
          <Label>Specialty</Label>
          <Value>{partner?.specialty || 'N/A'}</Value>
        </Field>
        
        <Field>
          <Label>Bio</Label>
          <Value>{partner?.bio || 'No bio provided'}</Value>
        </Field>
        
        {partner?.pendingApproval && (
          <ButtonContainer>
            <Button onClick={handleApprove}>Approve Partner</Button>
            <Button variant="outline" onClick={handleReject}>Reject Partner</Button>
          </ButtonContainer>
        )}
        
        {partner?.rejected && (
          <ButtonContainer>
            <Button onClick={handleApprove}>Approve Partner</Button>
          </ButtonContainer>
        )}
      </Card>
    </Container>
  );
};

export default PartnerDetail; 